import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Section, Loading, Empty, Pill, useFetch, dt, day } from "./_shared";
import { ChevronLeft, ChevronRight, AlertTriangle, Info, Receipt, Save } from "lucide-react";
function eur(cents) {
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CAT = {
  revenue_in: ["Einnahme", "good"],
  commission_in: ["Provision", "info"],
  payout_out: ["Auszahlung", "warn"],
  refund_out: ["Erstattung", "bad"],
  revenuecat_noncash: ["RC · kein Cash", "default"],
  uncounted: ["Nicht gezählt", "default"],
};
const CATEGORIES = [
  ["", "Alle Kategorien"],
  ["revenue_in", "Einnahmen"],
  ["commission_in", "Provisionen"],
  ["payout_out", "Auszahlungen"],
  ["refund_out", "Erstattungen"],
  ["revenuecat_noncash", "RC ohne Cash"],
  ["uncounted", "Nicht gezählt"],
];
const selCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground";

function RecRow({ label, value, sign = "", strong }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className={`text-xs ${strong ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{label}</span>
      <span className={`text-xs tabular-nums ${strong ? "font-semibold text-foreground" : "text-foreground"}`}>{sign}{eur(value)}</span>
    </div>
  );
}

function Reconcile({ s }) {
  const Row = RecRow;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="rounded-xl border border-border bg-background p-3" data-testid="admin-fin-reconcile">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Master-Cash-Abstimmung</p>
        <Row label="Einnahmen" value={s.revenue_in_cents} sign="+" />
        <Row label="Provisionen" value={s.commission_in_cents} sign="+" />
        <Row label="Auszahlungen" value={-s.payouts_out_cents} />
        <Row label="Erstattungen" value={-s.refunds_out_cents} />
        <Row label="Manuell (ein/aus)" value={s.manual_in_cents - s.manual_out_cents} />
        <div className="my-1 border-t border-border" />
        <Row label="= Master-Cash" value={s.master_cash_cents} strong />
        <p className="mt-1 text-[11px] text-muted-foreground">VAT-Reserve separat: {eur(s.vat_reserve_cents)} · Nicht gezählt: {eur(s.uncounted_cents)}</p>
      </div>
      <div className="rounded-xl border border-border bg-background p-3" data-testid="admin-fin-fees">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Brutto → Netto (nach Gebühren)</p>
        <Row label="Brutto-Umsatz" value={s.gross_revenue_cents} />
        <Row label="Apple-Gebühr" value={-s.fees_by_provider.apple} />
        <Row label="Google-Gebühr" value={-s.fees_by_provider.google} />
        <Row label="Stripe-Gebühr" value={-s.fees_by_provider.stripe} />
        <div className="my-1 border-t border-border" />
        <Row label="= Netto-Umsatz" value={s.net_revenue_cents} strong />
        <p className="mt-1 text-[11px] text-muted-foreground">Gebühren gesamt: {eur(s.total_fees_cents)} (geschätzt, siehe Tab „Pläne &amp; Gebühren“)</p>
      </div>
    </div>
  );
}

function Checks({ checks }) {
  if (!checks?.length) return null;
  return (
    <div className="space-y-2" data-testid="admin-fin-checks">
      {checks.map((c) => (
        <div key={c.id} className={`flex items-start gap-2 rounded-xl border p-3 ${c.severity === "warn" ? "border-amber-300 bg-amber-50" : "border-sky-200 bg-sky-50"}`} data-testid={`admin-fin-check-${c.id}`}>
          {c.severity === "warn" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> : <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />}
          <div>
            <p className={`text-xs font-semibold ${c.severity === "warn" ? "text-amber-800" : "text-sky-800"}`}>{c.title}</p>
            <p className={`text-xs ${c.severity === "warn" ? "text-amber-700" : "text-sky-700"}`}>{c.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TransactionsTab() {
  const [category, setCategory] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (category) params.set("category", category);
  if (kind) params.set("kind", kind);
  if (q) params.set("q", q);
  const { data, loading } = useFetch(`/admin/finance/transactions?${params.toString()}`);

  const s = data?.summary;
  const txs = data?.transactions || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className="space-y-4">
      {s && <Reconcile s={s} />}
      {data?.checks && <Checks checks={data.checks} />}

      <Section title={`Transaktionen${data ? ` · ${data.total}` : ""}`} testId="admin-fin-transactions">
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
          <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className={selCls} data-testid="admin-fin-tx-category">
            {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Input value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} placeholder="Art filtern (z. B. revenuecat)" data-testid="admin-fin-tx-kind" />
          <div className="flex gap-1 sm:col-span-2">
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setQ(qInput.trim()); setPage(1); } }} placeholder="E-Mail suchen …" data-testid="admin-fin-tx-q" />
            <Button variant="outline" className="rounded-lg" onClick={() => { setQ(qInput.trim()); setPage(1); }} data-testid="admin-fin-tx-search">Suchen</Button>
          </div>
        </div>

        {loading && !data ? <Loading /> : txs.length === 0 ? <Empty text="Keine Transaktionen für diesen Filter." /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-2">Datum</th>
                  <th className="py-2 pr-2">Art</th>
                  <th className="py-2 pr-2">Kategorie</th>
                  <th className="py-2 pr-2">Nutzer</th>
                  <th className="py-2 pr-2 text-right">Betrag</th>
                  <th className="py-2 pr-2 text-right">Cash</th>
                  <th className="py-2 pr-2 text-right">Gebühr/Netto</th>
                  <th className="py-2 pr-2">Referenz</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => {
                  const [lbl, tone] = CAT[t.category] || [t.category, "default"];
                  return (
                    <tr key={t.id} className="border-b border-border/60" data-testid={`admin-fin-tx-${t.id}`}>
                      <td className="py-2 pr-2 text-xs text-muted-foreground">{day(t.created_at)}</td>
                      <td className="py-2 pr-2 text-xs text-foreground">{t.kind}</td>
                      <td className="py-2 pr-2"><Pill tone={tone}>{lbl}</Pill></td>
                      <td className="py-2 pr-2 text-xs text-foreground">{t.party_email || <span className="text-muted-foreground">{t.party_id || "—"}</span>}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{eur(t.amount_cents)}</td>
                      <td className={`py-2 pr-2 text-right tabular-nums ${t.cash_effect_cents > 0 ? "text-emerald-600" : t.cash_effect_cents < 0 ? "text-rose-600" : "text-muted-foreground"}`}>{t.cash_effect_cents ? eur(t.cash_effect_cents) : "—"}</td>
                      <td className="py-2 pr-2 text-right text-xs tabular-nums text-muted-foreground">{t.fee_cents ? `${eur(t.fee_cents)} / ${eur(t.net_cents)}` : "—"}</td>
                      <td className="py-2 pr-2 text-[11px] text-muted-foreground">{t.reference || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && totalPages > 1 && (
          <div className="mt-3 flex items-center justify-center gap-3">
            <Button size="sm" variant="outline" className="gap-1 rounded-full" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-fin-tx-prev"><ChevronLeft className="h-4 w-4" /> Zurück</Button>
            <span className="text-xs text-muted-foreground">Seite {page} / {totalPages}</span>
            <Button size="sm" variant="outline" className="gap-1 rounded-full" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-fin-tx-next">Weiter <ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </Section>
    </div>
  );
}

const SOURCE_LABEL = { revenuecat: "RevenueCat (App-Store/Play)", stripe: "Stripe", founder_500: "Founder-500", credits: "Credits", other: "Sonstige" };

export function PayingCustomersTab() {
  const { data, loading } = useFetch("/admin/finance/paying-customers");
  const [showExcluded, setShowExcluded] = useState(false);
  if (loading && !data) return <Loading />;
  if (!data) return <Empty text="Keine Daten." />;
  const bySource = data.by_source || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="admin-fin-pc-count">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Zahlende Kunden</p>
          <p className="mt-1 font-display text-2xl font-semibold text-foreground">{data.paying_count}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">MRR (brutto)</p>
          <p className="mt-1 font-display text-2xl font-semibold text-foreground">{eur(data.mrr_gross_cents)}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">MRR (netto)</p>
          <p className="mt-1 font-display text-2xl font-semibold text-emerald-600">{eur(data.mrr_net_cents)}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ausgeschlossen</p>
          <p className="mt-1 font-display text-2xl font-semibold text-foreground">{data.excluded_count}</p>
        </div>
      </div>

      <Section title="MRR nach Quelle" testId="admin-fin-pc-bysource">
        {Object.keys(bySource).length === 0 ? <Empty text="Noch keine zahlenden Kunden." /> : (
          <div className="space-y-1">
            {Object.entries(bySource).map(([src, v]) => (
              <div key={src} className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2">
                <span className="text-sm text-foreground">{SOURCE_LABEL[src] || src} <span className="text-xs text-muted-foreground">· {v.count}</span></span>
                <span className="text-sm tabular-nums text-foreground">{eur(v.mrr_gross_cents)} <span className="text-xs text-muted-foreground">/ netto {eur(v.mrr_net_cents)}</span></span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Zahlende Kunden im Detail" testId="admin-fin-pc-list">
        {data.customers.length === 0 ? <Empty text="Aktuell keine echten zahlenden Kunden (nach Abzug von Founder-50, Marketing, internen & Test-Accounts)." /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-2">E-Mail</th><th className="py-2 pr-2">Tarif</th><th className="py-2 pr-2">Quelle</th>
                  <th className="py-2 pr-2 text-right">MRR brutto</th><th className="py-2 pr-2 text-right">MRR netto</th><th className="py-2 pr-2">Start</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((c, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="py-2 pr-2 text-xs text-foreground">{c.email}</td>
                    <td className="py-2 pr-2 text-xs"><Pill tone="info">{c.tier}</Pill></td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">{SOURCE_LABEL[c.source] || c.source}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">{eur(c.mrr_gross_cents)}</td>
                    <td className="py-2 pr-2 text-right tabular-nums text-emerald-600">{eur(c.mrr_net_cents)}</td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">{c.started_at ? day(c.started_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title={`Ausgeschlossene Accounts (${data.excluded_count})`}
        testId="admin-fin-pc-excluded"
        action={<Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setShowExcluded((v) => !v)} data-testid="admin-fin-pc-excluded-toggle">{showExcluded ? "Ausblenden" : "Anzeigen"}</Button>}
      >
        {!showExcluded ? (
          <p className="text-xs text-muted-foreground">Accounts mit aktivem Nicht-Free-Abo, die NICHT als zahlende Kunden zählen (Founder-50, Marketing, intern, Test, Comp). Klicke „Anzeigen“ für die Gründe.</p>
        ) : data.excluded.length === 0 ? <Empty text="Keine ausgeschlossenen Accounts." /> : (
          <div className="space-y-1">
            {data.excluded.map((e, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-1.5">
                <span className="text-xs text-foreground">{e.email} <span className="text-muted-foreground">· {e.tier}</span></span>
                <Pill tone="warn">{e.reason}</Pill>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function PlansFeesTab({ plans, onActivate }) {
  const { data: tiersData } = useFetch("/admin/finance/tiers");
  const { data: feeData, reload: reloadFees } = useFetch("/admin/finance/fee-model");
  const [fees, setFees] = useState(null);
  const [saving, setSaving] = useState(false);

  const current = fees || feeData || {};
  const setFee = (k, v) => setFees({ ...current, [k]: v });

  const saveFees = async () => {
    setSaving(true);
    try {
      await api.put("/admin/finance/fee-model", {
        fee_apple_pct: Number(current.fee_apple_pct),
        fee_google_pct: Number(current.fee_google_pct),
        fee_stripe_pct: Number(current.fee_stripe_pct),
        fee_stripe_fixed_cents: Number(current.fee_stripe_fixed_cents),
      });
      toast.success("Gebühren gespeichert.");
      await reloadFees();
      setFees(null);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Zahlungsanbieter-Gebühren" testId="admin-fin-feemodel">
        <p className="mb-3 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Receipt className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Diese Sätze bestimmen die Brutto→Netto-Rechnung in der Transaktions-Ansicht. Apple/Google: 15 % (Small Business &lt; 1 Mio $/Jahr), sonst 30 %. Stripe EU: ca. 1,5 % + 0,25 €. Der Master-Cash bleibt brutto — die Gebühren werden hier als Abzug transparent gemacht.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["fee_apple_pct", "Apple %"], ["fee_google_pct", "Google %"], ["fee_stripe_pct", "Stripe %"], ["fee_stripe_fixed_cents", "Stripe fix (Cent)"]].map(([k, l]) => (
            <div key={k}>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">{l}</label>
              <Input type="number" step="0.1" value={current[k] ?? ""} onChange={(e) => setFee(k, e.target.value)} data-testid={`admin-fin-fee-${k}`} />
            </div>
          ))}
        </div>
        <Button className="mt-3 gap-1.5 rounded-full" onClick={saveFees} disabled={saving} data-testid="admin-fin-fee-save"><Save className="h-4 w-4" /> {saving ? "…" : "Gebühren speichern"}</Button>
      </Section>

      <Section title="Abo-Tarife" testId="admin-fin-tiers">
        {!tiersData ? <Loading /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-2">Tarif</th><th className="py-2 pr-2">Schlüssel</th><th className="py-2 pr-2">Intervall</th><th className="py-2 pr-2 text-right">Preis</th>
                </tr>
              </thead>
              <tbody>
                {tiersData.tiers.map((t) => (
                  <tr key={t.key} className="border-b border-border/60">
                    <td className="py-2 pr-2 text-foreground">{t.name}</td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">{t.key}</td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">{t.interval}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">{t.price_eur_cents ? eur(t.price_eur_cents) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Finanzpläne" testId="admin-fin-plans">
        {(plans || []).length === 0 ? <Empty text="Keine Pläne hinterlegt." /> : (
          <div className="space-y-2">
            {plans.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{p.name || p.id}{p.active && <span className="ml-1 rounded bg-emerald-100 px-1.5 text-[10px] text-emerald-700">aktiv</span>}</p><p className="truncate text-xs text-muted-foreground">{p.kind || ""}{p.created_at ? ` · ${dt(p.created_at)}` : ""}</p></div>
                {!p.active && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onActivate(p.id)} data-testid={`admin-fin-activate-${p.id}`}>Aktivieren</Button>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
